/**
 * AI Accessibility Helpers
 * Provides functions for making AI-generated content accessible
 */
/**
 * Generate accessible label for AI recommendations
 */
export function generateAccessibleRecommendationLabel(title, confidence, reasoning, options = {}) {
    const { includeConfidence = true, includeExplanation = false, simplifyLanguage = false, } = options;
    let label = title;
    let description = `AI recommendation: ${title}`;
    if (includeConfidence) {
        const confidenceText = formatConfidenceForA11y(confidence);
        description += `. ${confidenceText}`;
    }
    if (includeExplanation && reasoning) {
        const simplifiedReasoning = simplifyLanguage
            ? simplifyTextForA11y(reasoning)
            : reasoning;
        description += `. Reason: ${simplifiedReasoning}`;
    }
    return {
        label,
        description,
        role: 'button',
        liveRegion: 'polite',
    };
}
/**
 * Generate accessible label for progress visualizations
 */
export function generateAccessibleProgressLabel(skillName, progress, trend, _options = {}) {
    const progressPercent = Math.round(progress * 100);
    const trendText = getTrendTextForA11y(trend);
    const label = `${skillName} progress: ${progressPercent}%`;
    const description = `Your ${skillName} skill is at ${progressPercent}% mastery and is ${trendText}`;
    return {
        label,
        description,
        role: 'progressbar',
        hint: 'Use arrow keys to explore skill details',
    };
}
/**
 * Generate accessible label for AI notifications
 */
export function generateAccessibleNotificationLabel(title, content, aiReasoning, priority, options = {}) {
    const { simplifyLanguage = false, maxLength = 200 } = options;
    let description = `${getPriorityTextForA11y(priority)} notification: ${title}. ${content}`;
    if (aiReasoning) {
        const reasoning = simplifyLanguage
            ? simplifyTextForA11y(aiReasoning)
            : aiReasoning;
        description += ` AI selected this because: ${reasoning}`;
    }
    // Truncate if too long
    if (description.length > maxLength) {
        description = description.substring(0, maxLength - 3) + '...';
    }
    return {
        label: title,
        description,
        role: 'alert',
        liveRegion: priority === 'high' ? 'assertive' : 'polite',
    };
}
/**
 * Generate accessible label for mastery visualizations
 */
export function generateAccessibleMasteryLabel(skills, _options = {}) {
    const totalSkills = skills.length;
    const masteredSkills = skills.filter(s => s.level > 0.8).length;
    const averageLevel = skills.reduce((sum, s) => sum + s.level, 0) / totalSkills;
    const label = `Skill mastery overview: ${masteredSkills} of ${totalSkills} skills mastered`;
    const description = `You have mastered ${masteredSkills} out of ${totalSkills} skills. ` +
        `Your average skill level is ${Math.round(averageLevel * 100)}%. ` +
        `Skills are organized by categories: ${[...new Set(skills.map(s => s.category))].join(', ')}.`;
    return {
        label,
        description,
        role: 'img',
        hint: 'Navigate through skills using tab key or touch gestures',
    };
}
/**
 * Generate accessible label for AI explanations
 */
export function generateAccessibleExplanationLabel(explanation, confidence, factors, options = {}) {
    const { simplifyLanguage = false, includeConfidence = true } = options;
    let processedExplanation = simplifyLanguage
        ? simplifyTextForA11y(explanation)
        : explanation;
    let description = `AI explanation: ${processedExplanation}`;
    if (includeConfidence) {
        const confidenceText = formatConfidenceForA11y(confidence);
        description += `. ${confidenceText}`;
    }
    if (factors.length > 0) {
        const topFactors = factors.slice(0, 3);
        description += `. Key factors: ${topFactors.join(', ')}.`;
    }
    return {
        label: 'AI explanation',
        description,
        role: 'region',
        liveRegion: 'polite',
    };
}
/**
 * Generate accessible label for confidence indicators
 */
export function generateAccessibleConfidenceLabel(confidence, context) {
    const confidenceText = formatConfidenceForA11y(confidence);
    const percentage = Math.round(confidence * 100);
    const label = `AI confidence: ${percentage}%`;
    let description = confidenceText;
    if (context) {
        description += ` for ${context}`;
    }
    // Add guidance based on confidence level
    if (confidence < 0.4) {
        description +=
            '. Consider providing more information for better recommendations.';
    }
    else if (confidence > 0.8) {
        description += '. This is a highly confident recommendation.';
    }
    return {
        label,
        description,
        role: 'status',
    };
}
/**
 * Format confidence for accessibility
 */
function formatConfidenceForA11y(confidence) {
    const percentage = Math.round(confidence * 100);
    if (confidence >= 0.8) {
        return `High confidence at ${percentage}%`;
    }
    else if (confidence >= 0.6) {
        return `Medium confidence at ${percentage}%`;
    }
    else if (confidence >= 0.4) {
        return `Low confidence at ${percentage}%`;
    }
    else {
        return `Very low confidence at ${percentage}%`;
    }
}
/**
 * Get trend text for accessibility
 */
function getTrendTextForA11y(trend) {
    switch (trend) {
        case 'improving':
            return 'getting better';
        case 'declining':
            return 'needs attention';
        case 'stable':
            return 'staying steady';
        default:
            return 'unchanged';
    }
}
/**
 * Get priority text for accessibility
 */
function getPriorityTextForA11y(priority) {
    switch (priority) {
        case 'high':
            return 'Important';
        case 'medium':
            return 'Moderate';
        case 'low':
            return 'Low priority';
        default:
            return 'Standard';
    }
}
/**
 * Simplify text for accessibility
 */
function simplifyTextForA11y(text) {
    return (text
        // Replace technical terms
        .replace(/algorithm/gi, 'system')
        .replace(/optimization/gi, 'improvement')
        .replace(/personalization/gi, 'customization')
        .replace(/correlation/gi, 'connection')
        .replace(/statistical/gi, 'data-based')
        .replace(/machine learning/gi, 'AI')
        // Simplify complex phrases
        .replace(/based on your historical patterns/gi, 'based on your past activity')
        .replace(/confidence interval/gi, 'certainty level')
        .replace(/predictive modeling/gi, 'prediction')
        // Remove filler words
        .replace(/\b(essentially|basically|fundamentally)\b/gi, '')
        // Shorten sentences
        .split('. ')
        .map(sentence => sentence.length > 100 ? sentence.substring(0, 97) + '...' : sentence)
        .join('. '));
}
/**
 * Generate keyboard navigation instructions
 */
export function generateKeyboardInstructions(componentType) {
    const baseInstructions = 'Use Tab to navigate, Enter to select, Escape to close.';
    switch (componentType) {
        case 'recommendation':
            return `${baseInstructions} Use arrow keys to browse alternatives.`;
        case 'progress':
            return `${baseInstructions} Use arrow keys to explore different skills.`;
        case 'mastery':
            return `${baseInstructions} Use arrow keys to navigate skill categories.`;
        case 'notification':
            return `${baseInstructions} Use Space to dismiss notification.`;
        default:
            return baseInstructions;
    }
}
/**
 * Generate screen reader announcements for dynamic content
 */
export function generateScreenReaderAnnouncement(type, message, priority = 'polite') {
    const prefix = {
        update: 'Update: ',
        error: 'Error: ',
        success: 'Success: ',
        info: 'Information: ',
    }[type];
    return {
        message: `${prefix}${message}`,
        priority: type === 'error' ? 'assertive' : priority,
    };
}
/**
 * Validate accessibility compliance
 */
export function validateAccessibility(content) {
    const issues = [];
    const suggestions = [];
    // Check label length
    if (content.label.length > 100) {
        issues.push('Label too long for screen readers');
        suggestions.push('Consider shortening the label to under 100 characters');
    }
    // Check description length
    if (content.description.length > 300) {
        issues.push('Description too long');
        suggestions.push('Consider breaking description into smaller chunks');
    }
    // Check for empty content
    if (!content.label.trim()) {
        issues.push('Empty label');
        suggestions.push('Provide a meaningful label');
    }
    // Check for appropriate role
    if (!content.role) {
        suggestions.push('Consider adding an appropriate ARIA role');
    }
    return {
        isValid: issues.length === 0,
        issues,
        suggestions,
    };
}
/**
 * Generate focus management instructions
 */
export function generateFocusInstructions(action) {
    switch (action) {
        case 'open':
            return 'Dialog opened. Focus moved to first interactive element.';
        case 'close':
            return 'Dialog closed. Focus returned to trigger element.';
        case 'navigate':
            return 'Use Tab and Shift+Tab to navigate between elements.';
        case 'select':
            return 'Item selected. Use Tab to continue or Escape to cancel.';
        default:
            return 'Focus management active.';
    }
}
